import { Injectable } from '@angular/core';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import Point from 'ol/geom/Point';
import * as olProj from 'ol/proj';
import * as olStyle from 'ol/style';
import { Style, Fill, Text } from 'ol/style';
import {
    tipoMarcatoreList,
    statoMarcatoreList,
    createPopUp,
    getFeatureCoordinatesParams,
    clusterSourceDistance,
} from '@core/utils/marcatore.utils';

import { map } from 'rxjs/operators';
import { Map } from 'ol';

import { MarcatoreService } from '@core/http-gen/services/marcatore.service';
import ClusterSource from 'ol/source/Cluster';

import { Observable } from 'rxjs';
import { FeatureLike } from 'ol/Feature';
import VectorLayer from 'ol/layer/Vector';

@Injectable({
    providedIn: 'root',
})
export class MarcatoriSourceService {
    constructor(private marcatoreService: MarcatoreService) {}

    getSource(tipoMarcatoreList: number[]): Observable<ClusterSource> {
        return this.marcatoreService
            .apiMarcatoreMarcatoreListByPosizionePost({
                body: {
                    topLeftPoint: { x: 10.519868, y: 46.452181 },
                    topRightPoint: { x: 12.119087, y: 46.576185 },
                    bottomLeftPoint: { x: 10.281776, y: 45.733835 },
                    bottomRightPoint: { x: 11.523124, y: 45.618032 },
                    tipoMarcatoreList: tipoMarcatoreList,
                },
            })
            .pipe(
                map((marcatori) => {
                    const features = marcatori.map((marcatore) => {
                        const feature = new Feature(new Point(olProj.fromLonLat([marcatore.posizione.x, marcatore.posizione.y])));
                        feature.set('tipoMarcatore', marcatore.tipoMarcatore);
                        feature.set('data', marcatore);
                        return feature;
                    });

                    const vectorSource = new VectorSource({
                        features: features,
                    });

                    const clusterSource = new ClusterSource({
                        distance: clusterSourceDistance,
                        source: vectorSource,
                    });

                    return clusterSource;
                }),
            );
    }

    openPupUp(map: Map | undefined, feature: FeatureLike | undefined): void {
        if (!map || !feature) {
            return;
        }

        const features = feature.get('features');
        if (!features || features.length === 0) {
            return;
        }

        if (features.length > 1) {
            createPopUp(map, feature, {
                title: features.length + ' Marcatori',
                description: features
                    .map((f: Feature) => {
                        const marcatoreData = f.get('data');
                        const marcatoreTipoLabel =
                            tipoMarcatoreList.find((tipo) => tipo.value === marcatoreData.tipoMarcatore)?.label || 'Marcatore';
                        const ultimoControlloDate = marcatoreData.ultimoControllo ? new Date(marcatoreData.ultimoControllo) : null;

                        const toReturn = [];
                        toReturn.push(`<p class="card-text mb-0 fw-bold">${marcatoreTipoLabel}: ${marcatoreData.nome}</p>`);
                        if (marcatoreData.descrizione) {
                            toReturn.push(`<p class="card-text mb-0">${marcatoreData.descrizione}</p>`);
                        }
                        toReturn.push(
                            `<p class="card-text mb-0">Stato: ${
                                statoMarcatoreList.find((s) => s.value === marcatoreData.statoMarcatore)?.label || 'Sconosciuto'
                            }</p>`,
                        );
                        if (marcatoreData.ultimoControllo) {
                            toReturn.push(
                                `<p class="card-text mb-0">Ultimo controllo: ${ultimoControlloDate?.toLocaleDateString('it-IT', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                })}</p>`,
                            );
                        }

                        return toReturn.join('');
                    })
                    .join('<hr />'),
                buttons: null,
            });
        } else {
            const marcatoreData = features[0].get('data');

            this.marcatoreService.apiMarcatoreMarcatoreByIdGet({ marcatoreId: marcatoreData.id }).subscribe((marcatore) => {
                const marcatoreTipoLabel = tipoMarcatoreList.find((tipo) => tipo.value === marcatore.tipoMarcatore)?.label || 'Marcatore';
                const ultimoControlloDate = marcatore.ultimoControllo ? new Date(marcatore.ultimoControllo) : null;
                createPopUp(map, feature, {
                    title: 'Marcatore',
                    description: [
                        `<p class="card-text mb-0 fw-bold">${marcatoreTipoLabel}: ${marcatore.nome}</p>`,
                        marcatore.descrizione ? `<p class="card-text mb-0">${marcatore.descrizione}</p>` : '',
                        `<p class="card-text mb-0">Stato: ${
                            statoMarcatoreList.find((s) => s.value === marcatore.statoMarcatore)?.label || 'Sconosciuto'
                        }</p>`,
                        marcatore.ultimoControllo
                            ? `<p class="card-text mb-0">Ultimo controllo: ${ultimoControlloDate?.toLocaleDateString('it-IT', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                              })}</p>`
                            : '',
                    ].join(''),
                    buttons: marcatore.hasPermissionToEdit
                        ? [
                              {
                                  label: 'Modifica',
                                  href: `/mappa/marcatore/${marcatore.id}/edit?${getFeatureCoordinatesParams(map, features[0])}`,
                                  newPage: false,
                              },
                          ]
                        : [],
                });
            });
        }
    }

    getClusterStyle(feature: FeatureLike): Style | Style[] {
        const ICON_SCALE = 0.5;

        const size = feature.get('features').length;

        let textSize = 20;
        if (size > 10) {
            textSize = 17;
        }
        if (size > 100) {
            textSize = 15;
        }
        if (size > 1000) {
            textSize = 13;
        }

        if (size > 1) {
            return [
                new Style({
                    image: new olStyle.Circle({
                        radius: 18,
                        fill: new Fill({ color: '#458f32' }),
                        stroke: new olStyle.Stroke({ color: '#fff', width: 2 }),
                    }),
                }),
                new Style({
                    text: new Text({
                        text: size.toString(),
                        font: `bold ${textSize}px sans-serif`,
                        fill: new Fill({ color: '#fff' }),
                        offsetY: 1,
                    }),
                }),
            ];
        } else {
            const marcatoreTipo = feature.get('features')[0].get('tipoMarcatore');

            return new olStyle.Style({
                image: new olStyle.Icon({
                    anchor: [0.5, 0.5],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                    scale: ICON_SCALE,
                    src: this.getIconByTipo(marcatoreTipo),
                }),
            });
        }
    }

    private getIconByTipo(tipoMarcatore: number): string {
        switch (tipoMarcatore) {
            case 1: // Idrante soprassuolo
                return 'assets/images/icons/map/fire-hydrant-soprasuolo.svg';
            case 2: // Idrante sottosuolo
                return 'assets/images/icons/map/fire-hydrant-sottosuolo.svg';
            case 3: // Piazzola elicottero
                return 'assets/images/icons/map/helicopter-pad.svg';
            case 4: // Bacino idrico
                return 'assets/images/icons/map/water-drops.svg';
            case 5: // Defibrillatore
                return 'assets/images/icons/map/defibrillator.svg';
            case 100: // Punto di interesse
                return 'assets/images/icons/map/warning.svg';
            default:
                return ''; // Icona di default
        }
    }

    filterByTipoMarcatore(allMarcatoreLayer: VectorLayer, tipoMarcatoreList: (string | number)[]): ClusterSource {
        // Normalize tipo list to numbers
        const tipoList = tipoMarcatoreList.map((tipo) => (typeof tipo === 'string' ? parseInt(tipo, 10) : tipo));

        const source = allMarcatoreLayer.getSource();

        if (!(source instanceof ClusterSource)) {
            return new ClusterSource({ source: new VectorSource() }); // empty fallback
        }

        const baseSource = source.getSource();

        if (!baseSource) {
            return new ClusterSource({ source: new VectorSource() }); // empty fallback
        }

        // Filter subfeatures based on tipoMarcatore
        const filteredFeatures = baseSource.getFeatures().filter((feature: Feature) => {
            const data = feature.get('data');
            return data && tipoList.includes(data.tipoMarcatore);
        });

        // Create new filtered VectorSource and cluster it
        const filteredVectorSource = new VectorSource({
            features: filteredFeatures,
        });

        return new ClusterSource({
            distance: source.getDistance(), // preserve clustering distance
            source: filteredVectorSource,
        });
    }
}
